import React, { useEffect, useMemo, useState } from 'react';
import { InnerProps } from '../dynamicFormTypes';
import { useClipboard } from '../../../hooks/useClipboard';
import { Controller, SubmitHandler, useForm, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import useFormulaRunner from '../../../hooks/useFormulaRunner';
import { resolveUploads } from '../../../utils/forms.utils';
import { uploadFormsFile } from '../../../api/forms.api';
import { useMessage } from '../../../hooks/useMessage';
import { Box, Button, Checkbox, CircularProgress, MenuItem, Snackbar, Stack, TextField, Typography } from '@mui/material';
import { Score } from '../../widgets';
import Weather from '../../widgets/Score/Weather/Weather';
import { MultipleList } from './MultipleList';
import { ButtonRadioGroup } from '../../radioButton/RadioButton';
import { DatePicker } from '@mui/x-date-pickers';
import dayjs, { Dayjs } from 'dayjs';
import ConfirmModal from '../../ConfirmModal/ConfirmModal';
import FileField from './formFields/FileField';
import FormFieldTooltip from './FormFieldTooltip';
import "./DynamicFormSkin2.scss";

const DynamicFormSkin2: React.FC<InnerProps> = ({
    formId,
    formName,
    fields,
    schema,
    defaults,
    onSubmit,
    onValuesChange,
    apiSubmitting,
    saveButtonLabel = 'Submit',
    hasCancelButton = false,
    hasResetButton = false,
    onCancel,
    onHideLeaves,
    shouldSeparateLeaves,
    activeLeaves,
    isSubmitted,
    skin,
    formTitle,
    copyLabel,
    rightTitle,
    insetLabel,
    cancelLabel,
}) => {
    const [ snackbarText, setSnackbarText ] = useState<string>('Copied!');
    const [ isSnackbarOpen, setIsSnackbarOpen ] = useState<boolean>(false);
    const { copyToClipboard } = useClipboard();
    const [ checkedLeaves, setCheckedLeaves ] = useState<{
        [key: string]: boolean,
    }>({});

    const {
        control,
        handleSubmit,
        getValues,
        reset,
        setValue,
        formState: { errors, isSubmitting },
    } = useForm({
        resolver: zodResolver(schema),
        defaultValues: defaults,
        mode: 'onBlur',
    });

    const [ allowEdits, setAllowEdits ] = React.useState(true);
    const [ confirmOpen, setConfirmOpen ] = React.useState(false);
    const pendingRef = React.useRef<null | {
        fieldId: string;
        value: any;
    }>(null);

    const confirmNo = () => {
        pendingRef.current = null;
        setConfirmOpen(false);
    };

    const confirmYes = () => {
        setAllowEdits(true);
        setConfirmOpen(false);

        const p = pendingRef.current;
        pendingRef.current = null;

        if (p) {
            setValue(p.fieldId as any, p.value, { shouldDirty: true, shouldTouch: true, shouldValidate: true });
        }

        onHideLeaves?.();
    };

    const formulaFields = useMemo(() => {
        const result: {
            id: string,
            formula: string,
        }[] = [];
        fields.forEach(field => {
            if (field.calculated && field.formula) {
                result.push({
                    id: field.id,
                    formula: field.formula,
                });
            }
        });

        return result;
    }, [ fields ]);
    const formulaRunner = useFormulaRunner();
    const watched = useWatch({ control });

    const fallbackMulti = React.useMemo(() => {
        const optionsPrecheckedValues: Array<string | number> = [];
        fields.forEach(field => {
            if (!field.options?.length) {
                return;
            }

            field.options.forEach(option => {
                if (option.checkedInQuestions) {
                    optionsPrecheckedValues.push(option.value as string);
                }
            });
        });

        return optionsPrecheckedValues;
    }, [ fields ]);

    useEffect(() => {
        fields.forEach((f) => {
            if (f.typeBehavior !== 'multiple') {
                return;
            }

            const current = getValues(f.id);
            if (!Array.isArray(current)) {
                setValue(f.id, fallbackMulti, {
                    shouldDirty: false,
                    shouldTouch: false,
                    shouldValidate: false,
                });
            }
        });
    }, [ fields, fallbackMulti, getValues, setValue ]);

    useEffect(() => {
        onValuesChange(watched || {});
    }, [ watched, onValuesChange ]);

    const optionsDefaultChecked = useMemo(() => {
        const isCheckedByDefault: {
            [key: string]: boolean
        } = {};
        fields.forEach(field => {
            if (field.type !== 'select' || field.typeBehavior !== 'multiple') {
                return;
            }

            field.options?.forEach(option => {
                isCheckedByDefault[option.label] = option.checkedByDefault;
            });
        });

        return { keys: Object.keys(isCheckedByDefault), values: isCheckedByDefault };
    }, [ fields ]);

    useEffect(() => {
        const leaves = { ...checkedLeaves };
        activeLeaves.forEach(leaf => {
            if (leaves[leaf.id] === undefined) {
                const leafKey = optionsDefaultChecked.keys.find(key => (
                    leaf.label || ''
                ).startsWith(key));
                if (!leafKey) {
                    leaves[leaf.id] = true;
                    return;
                }

                leaves[leaf.id] = optionsDefaultChecked.values[leafKey];
            }
        });
        setCheckedLeaves(leaves);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ activeLeaves ]);

    const requestChange = (fieldId: string, nextValue: any, apply: (v: any) => void) => {
        if (allowEdits) {
            apply(nextValue);
            return;
        }

        pendingRef.current = { fieldId, value: nextValue };
        setConfirmOpen(true);
    };

    const getFormulaResult = (formula: string): string => {
        const data = getValues();
        const mergedData = {};
        Object.keys(data).forEach(key => {
            // @ts-ignore
            mergedData[key] = { value: data[key], points: 1 };
        });
        const result = formulaRunner(formula, mergedData);
        return result as string;
    };

    const resetForm = () => {
        const oldValuesKeys = Object.keys(getValues());
        const newValues: {
            [key: string]: string
        } = {};
        oldValuesKeys.forEach(key => newValues[key] = '');

        reset(newValues);
        onHideLeaves?.();
    };

    const submit: SubmitHandler<any> = async (vals) => {
        const formulaValues: {
            [key: string]: string
        } = {};
        formulaFields.forEach(formulaField => {
            formulaValues[formulaField.id] = getFormulaResult(formulaField.formula);
        });

        const uploadedFileValues = await resolveUploads(
            formId,
            fields,
            vals,
            uploadFormsFile,
        );

        await onSubmit({ ...vals, ...uploadedFileValues, ...formulaValues });
        setAllowEdits(false);
        if (!hasResetButton) {
            resetForm();
        }
    };

    const onCopyToClipboard = async () => {
        let text = '';
        const keys = Object.keys(checkedLeaves);

        keys.forEach(key => {
            if (!checkedLeaves[key]) {
                return;
            }

            const leaf = activeLeaves.find(item => item.id === key);
            if (!leaf) {
                return;
            }

            const textParts = leaf.label.split('-|');
            text += textParts.join('\n') + '\n\n';
        });

        if (!text) {
            setSnackbarText('Nothing to copy!');
            setIsSnackbarOpen(true);
            return;
        }

        const copied = await copyToClipboard(text);
        if (copied) {
            setSnackbarText('Copied!');
            setIsSnackbarOpen(true);
        }
    };

    const hasSliderBar = activeLeaves.some(
        leaf => leaf.type === 'widget' && leaf.typeBehavior === 'sliderBar',
    );

    const visibleFields = fields.filter(f => !f.hidden);

    const skinConfig = {
        skin1: {
            mainDiv: {
                width: 585,
                minWidth: 585,
                border: '1px solid #013b71',
                background: '#013b71',
                color: '#fff',
                fontWeight: 700,
                fontSize: '20px',
                padding: 8,
            },
            box: {
                width: '100%',
                px: 2,
                py: 1.25,
                border: '1px solid rgba(0, 0, 0, 0.23)',
                backgroundColor: '#fff',
                borderRadius: 1,
                color: 'text.primary',
                fontSize: '1rem',
                fontFamily: '"Roboto","Helvetica","Arial",sans-serif',
                lineHeight: '1.4375em',
                padding: 0,
            },
        },
        skin2: {
            mainDiv: {
                width: 585,
                minWidth: 585,
                border: '1px solid #013b71',
                background: '#013b71',
                color: '#fff',
                fontWeight: 700,
                fontSize: '16px',
                padding: 8,
            },
            box: {
                width: '100%',
                px: 2,
                py: 1.25,
                border: '1px solid rgba(0, 0, 0, 0.23)',
                backgroundColor: '#fff',
                borderRadius: 1,
                color: 'text.primary',
                fontSize: '1rem',
                fontFamily: '"Roboto","Helvetica","Arial",sans-serif',
                lineHeight: '1.4375em',
                padding: 0,
            },
        },
        skin3: {
            mainDiv: {
                width: 585,
                minWidth: 585,
                border: '1px solid #013b71',
                background: '#013b71',
                color: '#fff',
                fontWeight: 700,
                fontSize: '16px',
                padding: 8,
            },
            box: {
                width: '100%',
                px: 2,
                py: 1.25,
                border: '1px solid rgba(0, 0, 0, 0.23)',
                backgroundColor: '#fff',
                borderRadius: 1,
                color: 'text.primary',
                fontSize: '1rem',
                fontFamily: '"Roboto","Helvetica","Arial",sans-serif',
                lineHeight: '1.4375em',
                padding: 0,
            },
        },
        skin4: {
            mainDiv: {
                width: '100%',
                minWidth: 300,
                border: 'none',
                background: '#fff',
                color: '#000',
                fontWeight: 400,
                fontSize: '20px',
                padding: 8,
            },
            box: {
                width: '100%',
                px: 2,
                py: 1.25,
                border: 'none',
                backgroundColor: '#d1ecf1',
                borderRadius: 2,
                color: 'text.primary',
                fontSize: '22px',
                fontFamily: '"Roboto","Helvetica","Arial",sans-serif',
                lineHeight: '1.4375em',
                padding: '16px',
            },
        },
    };

    const config = skinConfig[skin];
    const { showMessage, MessageContainer } = useMessage();

    const isMashup837 = window.location.pathname.includes('mashups/837');
    const screenWidth = window.innerWidth;

    return (
        <>
            <div style={{
                width: config.mainDiv?.width || 600,
                minWidth: config.mainDiv?.minWidth || 600,
            }}>
                <div style={{ border: config.mainDiv?.border }}>
                    <div
                        style={{
                            background: config.mainDiv?.background,
                            color: config.mainDiv?.color,
                            fontWeight: config.mainDiv?.fontWeight,
                            padding: config.mainDiv?.padding,
                            fontSize: config.mainDiv?.fontSize,
                        }}
                    >
                        {formTitle || formName || 'Fill in the form'}
                    </div>
                    <Box
                        component="form"
                        onSubmit={handleSubmit(submit)}
                        noValidate
                        style={{
                            backgroundColor: '#f8fafc',
                            padding: 16,
                        }}
                    >
                        <Stack spacing={2} sx={{ display: 'flex', flexWrap: 'wrap' }}>
                            {visibleFields.map((f) => {
                                if (f.type === 'video' || f.type === 'image' || f.type === 'document') {
                                    return (
                                        <Controller
                                            key={f.id}
                                            name={f.id}
                                            control={control}
                                            render={({ field }) => (
                                                <FileField
                                                    value={field.value as File ?? null}
                                                    label={f.label}
                                                    error={errors[f.id]?.message as string}
                                                    onChange={field.onChange}
                                                    src={f.src}
                                                />
                                            )}
                                        />
                                    );
                                }
                                if (f.type === 'widget' && f.typeBehavior === 'sliderBar') {
                                    let score = '0';
                                    const sourceField = fields.find(field => field.id === f.source);
                                    if (sourceField) {
                                        if (sourceField.formula) {
                                            score = getFormulaResult(sourceField.formula);
                                        } else {
                                            const sourceValue = getValues()[f.source] as string;
                                            score = sourceValue ?? '0';
                                        }
                                    }

                                    return (
                                        <div key={f.id} className='sec_dynamic_form_main_container'>
                                            <Score score={score}/>
                                        </div>
                                    );
                                }
                                if (f.type === 'widget' && f.typeBehavior === 'weather') {
                                    let city = '';
                                    const sourceField = fields.find(field => field.id === f.source);

                                    if (sourceField) {
                                        if (sourceField.formula) {
                                            city = getFormulaResult(sourceField.formula);
                                        } else {
                                            const sourceValue = getValues()[f.source] as string;
                                            city = sourceValue ?? '';
                                        }
                                    }

                                    if (!city) {
                                        return null;
                                    }

                                    return (
                                        <div key={f.id} className='sec_dynamic_form_main_container'>
                                            <Weather city={city}/>
                                        </div>
                                    );
                                }
                                if (f.type === 'string') {
                                    const textParts = f.label.split('-|');
                                    return (
                                        <div key={f.id} className='sec_dynamic_form_main_container'>
                                            <Box
                                                sx={{
                                                    width: config.box?.width,
                                                    px: config.box?.px,
                                                    py: config.box?.py,
                                                    border: config.box?.border,
                                                    borderRadius: config.box?.borderRadius,
                                                    backgroundColor: config.box?.backgroundColor,
                                                    color: config.box?.color,
                                                    fontSize: config.box?.fontSize,
                                                    fontFamily: config.box?.fontFamily,
                                                    lineHeight: config.box?.lineHeight,
                                                    '&:hover': {
                                                        borderColor: 'text.primary',
                                                    },
                                                }}
                                            >
                                                {textParts.map((text, i) => (
                                                    <>
                                                        {text}
                                                        {i !== textParts.length - 1 && (
                                                            <br/>
                                                        )}
                                                    </>
                                                ))}
                                            </Box>
                                        </div>
                                    );
                                }
                                if (f.type === 'text' || f.type === 'email' || f.type === 'number' || f.type === 'password') {
                                    return (
                                        <Controller
                                            key={f.id}
                                            name={f.id}
                                            control={control}
                                            render={({ field }) => (
                                                <div className='sec_dynamic_form_main_container'>
                                                    <div className='sec_dynamic_form_main_container_label'>
                                                        {f.label}
                                                        {f.infoLink && (
                                                            <FormFieldTooltip url={f.infoLink}/>
                                                        )}
                                                    </div>
                                                    <div>
                                                        <TextField
                                                            {...field}
                                                            label={f.label}
                                                            type={f.type}
                                                            error={!!errors[f.id]}
                                                            helperText={(
                                                                errors[f.id]?.message as string
                                                            ) || ''}
                                                            sx={{ width: 300 }}
                                                            size="small"
                                                            onChange={(e) => requestChange(f.id, e.target.value, field.onChange)}
                                                        />
                                                    </div>
                                                </div>
                                            )}
                                        />
                                    );
                                }
                                if (f.type === 'url') {
                                    return (
                                        <div key={f.id} className='sec_dynamic_form_main_container'>
                                            <Box
                                                sx={{
                                                    width: '100%',
                                                    px: 2,
                                                    py: 1.25,
                                                    color: 'text.primary',
                                                    fontSize: '1rem',
                                                    fontFamily: '"Roboto","Helvetica","Arial",sans-serif',
                                                    lineHeight: '1.4375em',
                                                    textDecoration: 'underline',
                                                    cursor: 'pointer',
                                                }}
                                                onClick={() => window.open(f.label)}
                                            >
                                                {f.label}
                                            </Box>
                                        </div>
                                    );
                                }
                                if (f.type === 'select') {
                                    return (
                                        <Controller
                                            key={f.id}
                                            name={f.id}
                                            control={control}
                                            render={({ field }) => (
                                                <div className='sec_dynamic_form_main_container'>
                                                    {f.typeBehavior === 'multiple' ? (
                                                        <MultipleList<string | number>
                                                            label={f.label}
                                                            // @ts-ignore
                                                            options={f.options || []}
                                                            value={Array.isArray(field.value) ? field.value : []}
                                                            onChange={(v) => requestChange(f.id, v, field.onChange)}
                                                        />
                                                    ) : (
                                                        <>
                                                            <div className='sec_dynamic_form_main_container_label'>
                                                                {f.label}
                                                                {f.infoLink && (
                                                                    <FormFieldTooltip url={f.infoLink}/>
                                                                )}
                                                            </div>
                                                            <div>
                                                                <TextField
                                                                    {...field}
                                                                    select
                                                                    label={field.value ? '' : f.label}
                                                                    error={!!errors[f.id]}
                                                                    helperText={(
                                                                        errors[f.id]?.message as string
                                                                    ) || ''}
                                                                    onChange={(e) => requestChange(f.id, e.target.value, field.onChange)}
                                                                    sx={{
                                                                        width: 300,
                                                                        '& .MuiFormLabel-root:not(.Mui-focused)': {
                                                                            transform: 'translate(14px, 8.5px) scale(1)',
                                                                        },
                                                                        '& .MuiSelect-select': {
                                                                            py: 1, display: 'flex',
                                                                            alignItems: 'center',
                                                                        },
                                                                    }}
                                                                >
                                                                    {(
                                                                        f.options || []
                                                                    ).map((o) => (
                                                                        <MenuItem key={String(o.value)}
                                                                                  value={o.value as any}>
                                                                            {o.label}
                                                                        </MenuItem>
                                                                    ))}
                                                                </TextField>
                                                            </div>
                                                        </>
                                                    )}
                                                </div>
                                            )}
                                        />
                                    );
                                }
                                if (f.type === 'radio') {
                                    return (
                                        <Controller
                                            key={f.id}
                                            name={f.id}
                                            control={control}
                                            render={({ field }) => (
                                                <div className='sec_dynamic_form_main_container'>
                                                    <div className='sec_dynamic_form_main_container_label'>
                                                        {f.label}
                                                        {f.infoLink && (
                                                            <FormFieldTooltip url={f.infoLink}/>
                                                        )}
                                                    </div>
                                                    <div>
                                                        <ButtonRadioGroup
                                                            options={(
                                                                f.options || []
                                                            )}
                                                            value={field.value as any}
                                                            onChange={(e) => requestChange(f.id, e, field.onChange)}
                                                            emptyDefaultValue
                                                        />
                                                    </div>
                                                </div>
                                            )}
                                        />
                                    );
                                }
                                if (f.type === 'date') {
                                    return (
                                        <Controller
                                            key={f.id}
                                            name={f.id}
                                            control={control}
                                            render={({ field }) => (
                                                <div className='sec_dynamic_form_main_container'>
                                                    <div className='sec_dynamic_form_main_container_label'>
                                                        {f.label}
                                                        {f.infoLink && (
                                                            <FormFieldTooltip url={f.infoLink}/>
                                                        )}
                                                    </div>
                                                    <div>
                                                        <DatePicker
                                                            label={f.label}
                                                            value={field.value ? dayjs(field.value as string | number | Date | Dayjs) : null}
                                                            onChange={(v) => {
                                                                if (!v || !dayjs(v).isValid()) {
                                                                    field.onChange(null);
                                                                    return;
                                                                }

                                                                requestChange(
                                                                    f.id, dayjs(v)
                                                                    .toDate()
                                                                    .toISOString(),
                                                                    field.onChange,
                                                                );
                                                            }}
                                                            slotProps={{
                                                                textField: {
                                                                    error: !!errors[f.id],
                                                                    helperText: (
                                                                        errors[f.id]?.message as string
                                                                    ) || '',
                                                                    fullWidth: true,
                                                                },
                                                            }}
                                                            sx={{
                                                                width: 300,
                                                                '& .MuiFormLabel-root:not(.Mui-focused)': {
                                                                    transform: 'translate(14px, 8.5px) scale(1)',
                                                                },
                                                                '& .MuiPickersSectionList-root': {
                                                                    py: 1,
                                                                },
                                                            }}
                                                        />
                                                    </div>
                                                </div>
                                            )}
                                        />
                                    );
                                }
                                if (f.type === 'color') {
                                    return (
                                        <Controller
                                            key={f.id}
                                            name={f.id}
                                            control={control}
                                            render={({ field }) => (
                                                <div className='sec_dynamic_form_main_container'>
                                                    <div className='sec_dynamic_form_main_container_label'>
                                                        {f.label}
                                                        {f.infoLink && (
                                                            <FormFieldTooltip url={f.infoLink}/>
                                                        )}
                                                    </div>
                                                    <div>
                                                        <Box>
                                                            <Typography variant="caption"
                                                                        sx={{ mb: 0.5, display: 'block' }}>
                                                                {f.label}
                                                            </Typography>
                                                            <TextField
                                                                type="color"
                                                                value={field.value || '#000000'}
                                                                onChange={(e) => requestChange(f.id, e.target.value, field.onChange)}
                                                                sx={{
                                                                    width: 48,
                                                                    height: 48,
                                                                    p: 0,
                                                                    '& .MuiInputBase-root': {
                                                                        p: 0, height: '100%', borderRadius: 1,
                                                                    },
                                                                    '& fieldset': { display: 'none' },
                                                                    '& input[type="color"]': {
                                                                        cursor: 'pointer',
                                                                        padding: 0,
                                                                        border: 'none',
                                                                        width: '100%',
                                                                        height: '100%',
                                                                        background: 'none',
                                                                        borderRadius: 1,
                                                                        WebkitAppearance: 'none',
                                                                    },
                                                                    '& input[type="color"]::-webkit-color-swatch-wrapper': {
                                                                        padding: 0,
                                                                        borderRadius: 1,
                                                                    },
                                                                    '& input[type="color"]::-webkit-color-swatch': {
                                                                        border: '1px solid rgba(0,0,0,0.35)',
                                                                        borderRadius: 4,
                                                                    },
                                                                }}
                                                            />
                                                            {!!errors[f.id] && (
                                                                <Typography variant="caption" color="error">
                                                                    {(
                                                                        errors[f.id]?.message as string
                                                                    ) || ''}
                                                                </Typography>
                                                            )}
                                                        </Box>
                                                    </div>
                                                </div>
                                            )}
                                        />
                                    );
                                }
                                if (f.type === 'formula') {
                                    return (
                                        <div key={f.id} className='sec_dynamic_form_main_container'>
                                            <div className='sec_dynamic_form_main_container_label'>
                                                {f.label}
                                                {f.infoLink && (
                                                    <FormFieldTooltip url={f.infoLink}/>
                                                )}
                                            </div>
                                            <div>
                                                <Box
                                                    sx={{
                                                        width: '90%',
                                                        px: 2,
                                                        py: 1.25,
                                                        border: '1px solid rgba(0, 0, 0, 0.23)',
                                                        borderRadius: 1,
                                                        color: 'text.primary',
                                                        fontSize: '1rem',
                                                        fontFamily: '"Roboto","Helvetica","Arial",sans-serif',
                                                        lineHeight: '1.4375em',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: 1,
                                                    }}
                                                >
                                                    {getFormulaResult(f.formula)}
                                                </Box>
                                            </div>
                                        </div>
                                    );
                                }
                                return null;
                            })}
                            {hasCancelButton ? (
                                <div className='sec_dynamic_form_main_btn_container'>
                                    <Button
                                        startIcon={isSubmitting || apiSubmitting ?
                                            <CircularProgress size={16}/> : undefined}
                                        variant="outlined"
                                        disabled={isSubmitting || apiSubmitting}
                                        onClick={onCancel}
                                        sx={{ width: '49%' }}
                                    >
                                        {cancelLabel || 'Cancel'}
                                    </Button>
                                    <Button
                                        startIcon={isSubmitting || apiSubmitting ?
                                            <CircularProgress size={16}/> : undefined}
                                        type="submit"
                                        variant="contained"
                                        disabled={isSubmitting || apiSubmitting}
                                        sx={{ width: '49%', backgroundColor: '#013b71' }}
                                    >
                                        {saveButtonLabel}
                                    </Button>
                                </div>
                            ) : hasResetButton ? (
                                <div className='sec_dynamic_form_main_btn_container'>
                                    <Button
                                        startIcon={isSubmitting || apiSubmitting ?
                                            <CircularProgress size={16}/> : undefined}
                                        variant="outlined"
                                        disabled={isSubmitting || apiSubmitting}
                                        onClick={resetForm}
                                        sx={{ width: '49%' }}
                                    >
                                        {cancelLabel || 'Reset'}
                                    </Button>
                                    <Button
                                        startIcon={isSubmitting || apiSubmitting ?
                                            <CircularProgress size={16}/> : undefined}
                                        type="submit"
                                        variant="contained"
                                        disabled={isSubmitting || apiSubmitting}
                                        sx={{ width: '49%', backgroundColor: '#013b71' }}
                                    >
                                        {insetLabel || saveButtonLabel}
                                    </Button>
                                </div>
                            ) : skin === 'skin4' && visibleFields.length === 1 && visibleFields[0].type === 'formula' ? (
                                <>
                                    <Button
                                        onClick={() => {
                                            navigator.clipboard.writeText(getFormulaResult(visibleFields[0].formula));
                                            showMessage('Text copied to clipboard!', 'success');
                                        }}
                                        className='sec_dynamic_form_main_btn_container_copy'>
                                        Copy To Clipboard
                                    </Button>
                                    <MessageContainer/>
                                </>
                            ) : skin === 'skin4' ? (
                                <></>
                            ) : (
                                <Button
                                    type="submit"
                                    variant="contained"
                                    disabled={isSubmitting || apiSubmitting}
                                    startIcon={isSubmitting || apiSubmitting ?
                                        <CircularProgress size={16}/> : undefined}
                                    style={{ backgroundColor: '#013b71' }}
                                >
                                    {insetLabel}
                                </Button>
                            )}
                        </Stack>
                    </Box>
                    <ConfirmModal
                        title="Change submitted data?"
                        message="This form was already submitted. Are you sure you want to change the submitted data?"
                        open={confirmOpen}
                        onConfirm={confirmYes}
                        onCancel={confirmNo}
                    />
                </div>
            </div>
            {shouldSeparateLeaves && activeLeaves.length > 0 && isSubmitted ? (
                <div style={{
                    border: hasSliderBar ? 'none' : '1px solid #013b71',
                    marginLeft: isMashup837 && screenWidth < 1476 && screenWidth > 1430 ? '48px' : '20px',
                    maxWidth : '800px',
                }}>
                    <div className='sec_dynamic_form_main_right_container_title'>
                        {rightTitle || ''}
                    </div>
                    <Stack spacing={2} sx={{ display: 'flex', flexWrap: 'wrap' }}>
                        {activeLeaves.map(leaf => {
                            if (leaf.type === 'string') {
                                const textParts = leaf.label.split('-|');
                                const isChecked = Boolean(checkedLeaves[leaf.id]);

                                return (
                                    <div key={leaf.id} className='sec_dynamic_form_main_right_container'>
                                        <Checkbox
                                            size="small"
                                            style={{ position: 'absolute', left: 0, top: 3 }}
                                            value={isChecked}
                                            checked={isChecked}
                                            onChange={(_, checked) => {
                                                setCheckedLeaves(leaves => (
                                                    {
                                                        ...leaves,
                                                        [leaf.id]: checked,
                                                    }
                                                ));
                                            }}
                                        />
                                        <Box
                                            sx={{
                                                width: '100%',
                                                py: 1.25,
                                                paddingLeft: 4,
                                                paddingRight: 2,
                                                border: '1px solid rgba(0, 0, 0, 0.23)',
                                                borderRadius: 1,
                                                color: 'text.primary',
                                                fontSize: '1rem',
                                                fontFamily: '"Roboto","Helvetica","Arial",sans-serif',
                                                lineHeight: '1.4375em',
                                                '&:hover': {
                                                    borderColor: 'text.primary',
                                                },
                                            }}
                                        >
                                            {textParts.map((text, i) => (
                                                <>
                                                    {text}
                                                    {i !== textParts.length - 1 && (
                                                        <br/>
                                                    )}
                                                </>
                                            ))}
                                        </Box>
                                    </div>
                                );
                            } else if (leaf.type === 'image') {
                                return (
                                    <div key={leaf.id} className='sec_dynamic_form_main_right_container'>
                                        <FileField
                                            alignLeft
                                            value={null}
                                            label=""
                                            src={leaf.src}
                                            onChange={() => {
                                            }}
                                        />
                                    </div>
                                );
                            } else if (leaf.type === 'widget' && leaf.typeBehavior === 'sliderBar') {
                                let score = '0';
                                const sourceField = fields.find(field => field.id === leaf.source);
                                if (sourceField) {
                                    if (sourceField.formula) {
                                        score = getFormulaResult(sourceField.formula);
                                    } else {
                                        const sourceValue = getValues()[leaf.source] as string;
                                        score = sourceValue ?? '0';
                                    }
                                }
                                return (
                                    <div key={leaf.id} className='sec_dynamic_form_main_right_container'>
                                        <Score score={score}/>
                                    </div>
                                );
                            } else if (leaf.type === 'url') {
                                return (
                                    <div key={leaf.id} className='sec_dynamic_form_main_right_container'>
                                        <Box
                                            sx={{
                                                width: '100%',
                                                px: 2,
                                                py: 1.25,
                                                color: 'text.primary',
                                                fontSize: '1rem',
                                                fontFamily: '"Roboto","Helvetica","Arial",sans-serif',
                                                lineHeight: '1.4375em',
                                                textDecoration: 'underline',
                                                cursor: 'pointer',
                                                paddingLeft: 4,
                                                paddingRight: 2,
                                                border: '1px solid rgba(0, 0, 0, 0.23)',
                                                borderRadius: 1,
                                                '&:hover': {
                                                    borderColor: 'text.primary',
                                                    textDecoration: 'none',
                                                },

                                            }}
                                            onClick={() => window.open(leaf.label)}
                                        >
                                            {leaf.name}
                                        </Box>
                                    </div>
                                );
                            }

                            return null;
                        })}

                        {!hasSliderBar && (
                            <Button
                                type="submit"
                                variant="contained"
                                style={{ backgroundColor: '#013b71', margin: '0 16px 16px' }}
                                onClick={onCopyToClipboard}
                            >
                                {copyLabel || ''}
                            </Button>
                        )}

                        <Snackbar
                            open={isSnackbarOpen}
                            onClose={() => setIsSnackbarOpen(false)}
                            autoHideDuration={1500}
                            message={snackbarText}
                            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                        />
                    </Stack>
                </div>
            ) : null}
        </>
    );
};

export default DynamicFormSkin2;
